import {BASE_URL_API} from '@/shared/functions/Functions';
import Image from 'next/image';
import Link from 'next/link';
import CardLayout from '@/shared/components/cards/CardLayout';
import {notFound} from 'next/navigation';
import EventCard from './EventCard';
import ManageStatusFilter from '@/shared/components/ManageStatusFilter';
import ManageStatusesLegend from '@/shared/components/OfferStatusLegend';

export const dynamic = 'force-dynamic';

const PAGE_SIZE = 15;

const STATUS_OPTIONS = [
    {value: 'proposed', label: 'Proposed', colorClass: 'proposed-dot'},
    {value: 'scheduled', label: 'Scheduled', colorClass: 'info-dot'},
    {value: 'feedback_loop', label: 'Feedback loop', colorClass: 'feedback_loop-dot'},
    {value: 'approved', label: 'Approved', colorClass: 'primary-dot'},
    {value: 'reported', label: 'Reported', colorClass: 'success-dot'},
];


type Params = Promise<{ uuid: string }>;
type SearchParams = Promise<Record<string, string | string[] | undefined>>;

const SORT_KEYS = [
    'displayName',
    'postingDate',
    'socialPlatformId',
    'updatedAt',
] as const;
type SortKey = (typeof SORT_KEYS)[number];

async function getManageData(uuid: string, params: URLSearchParams) {
    const url = `${BASE_URL_API}/public/manage/full/${uuid}?${params.toString()}`;
    const res = await fetch(url, {cache: 'no-store'});
    if (!res.ok) {
        notFound();
    }
    return res.json();
}

const buildHref = (
    uuid: string,
    current: Record<string, string | undefined>,
    patch: Record<string, string | undefined>
) => {
    const next = {...current, ...patch};
    const params = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => {
        if (v !== undefined && v !== '') params.set(k, v);
    });
    const qs = params.toString();
    return `/manage/${uuid}${qs ? `?${qs}` : ''}`;
};

const SortHeader = ({
                        uuid,
                        current,
                        sortKey,
                        label,
                        align = 'left',
                    }: {
    uuid: string;
    current: Record<string, string | undefined>;
    sortKey: SortKey;
    label: string;
    align?: 'left' | 'center';
}) => {
    const isActive = current.sortCriteria === sortKey;
    const nextDir = isActive && current.sortType === 'ASC' ? 'DESC' : 'ASC';
    const arrow = isActive ? (current.sortType === 'ASC' ? ' ↑' : ' ↓') : '';

    return (
        <th className={align === 'center' ? 'text-center' : ''}>
            <Link
                href={buildHref(uuid, current, {
                    sortCriteria: sortKey,
                    sortType: nextDir,
                    page: '1',
                })}
                className='text-reset text-decoration-none'
            >
                {label}
                {arrow}
            </Link>
        </th>
    );
};

export default async function ManagePage(props: {
    params: Params;
    searchParams: SearchParams;
}) {
    const {uuid} = await props.params;
    const sp = await props.searchParams;

    const currentStatuses =
        typeof sp.status === 'string' ? sp.status.split(',').filter(Boolean) : [];

    const current = {
        page: typeof sp.page === 'string' ? sp.page : '1',
        status: currentStatuses.join(','),
        platformId: typeof sp.platformId === 'string' ? sp.platformId : '',
        sortCriteria:
            typeof sp.sortCriteria === 'string' ? sp.sortCriteria : 'postingDate',
        sortType: typeof sp.sortType === 'string' ? sp.sortType : 'DESC',
    };

    const apiParams = new URLSearchParams();
    Object.entries(current).forEach(([k, v]) => {
        if (v) apiParams.set(k, v);
    });
    apiParams.set('take', String(PAGE_SIZE));

    const {campaign, events, pagination} = await getManageData(uuid, apiParams);

    const campaignImage = campaign?.campaignImages?.find((img: any) =>
        img?.mime?.startsWith('image/')
    );

    const page = pagination?.page ?? 1;
    const perPage = pagination?.perPage ?? PAGE_SIZE;
    const total = pagination?.total ?? events?.length ?? 0;
    const totalPages = pagination?.totalPages ?? 1;

    const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
    const rangeEnd = Math.min(page * perPage, total);

    return (
        <div className='container mx-auto my-5 px-2 pb-4'>
            {/* Header + status filter on one row */}
            <div className="p-3">
                <CardLayout
                    className='d-flex flex-wrap align-items-center justify-content-between'
                >
                    <div className="d-flex flex-wrap justify-content-between align-items-center">
                        <div className='d-flex align-items-center mb-2 mb-lg-0'>
                            {campaignImage?.thumbnail && (
                                <Image
                                    className='rounded mr-3 flex-shrink-0'
                                    src={campaignImage.thumbnail}
                                    alt={campaign.title}
                                    width={64}
                                    height={64}
                                    style={{objectFit: 'cover'}}
                                />
                            )}
                            <h4 className='mb-0'>{campaign?.title}</h4>
                        </div>
                        <div>
                            <ManageStatusFilter
                                uuid={uuid}
                                currentStatuses={currentStatuses}
                                current={current}
                                statusOptions={STATUS_OPTIONS}
                            />
                        </div>
                    </div>
                </CardLayout>
            </div>
            {/* Table */}
            {events?.length ? (
                <>
                    <div className='table-responsive p-3'>
                        <table className='table table-vertical-center mb-0'>
                            <thead>
                            <tr>
                                <SortHeader
                                    uuid={uuid}
                                    current={current}
                                    sortKey='displayName'
                                    label='Influencer'
                                />
                                <SortHeader
                                    uuid={uuid}
                                    current={current}
                                    sortKey='postingDate'
                                    label='Posting date'
                                    align='center'
                                />
                                <SortHeader
                                    uuid={uuid}
                                    current={current}
                                    sortKey='socialPlatformId'
                                    label='Platform'
                                    align='center'
                                />
                                <th className='text-center'>Status</th>
                            </tr>
                            </thead>
                            <tbody className="p-3">
                            {events.map((event: any) => (
                                <EventCard key={event.id} event={event}/>
                            ))}
                            </tbody>
                        </table>
                    </div>
                        <ManageStatusesLegend statusOptions={STATUS_OPTIONS}/>
                    {/* Pagination */}
                    <div className='d-flex flex-column align-items-center mt-2 mb-3'>
                        <div className='small text-muted mb-2'>
                            Showing {rangeStart}–{rangeEnd} of {total} ({perPage} per
                            page)
                        </div>
                        {totalPages > 1 && (
                            <div className='d-flex' style={{gap: 6}}>
                                <Link
                                    href={buildHref(uuid, current, {
                                        page: String(Math.max(1, page - 1)),
                                    })}
                                    className={`btn btn-sm btn-outline-secondary ${
                                        page <= 1 ? 'disabled' : ''
                                    }`}
                                >
                                    Previous
                                </Link>
                                <span className='align-self-center text-muted'>
                      Page {page} of {totalPages}
                    </span>
                                <Link
                                    href={buildHref(uuid, current, {
                                        page: String(Math.min(totalPages, page + 1)),
                                    })}
                                    className={`btn btn-outline-secondary ${
                                        page >= totalPages ? 'disabled' : ''
                                    }`}
                                >
                                    Next
                                </Link>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <p className='text-muted text-center my-5'>
                    {currentStatuses.length === 0 && page === 1
                        ? 'This campaign has no events yet.'
                        : 'No events match these filters.'}
                </p>
            )}
        </div>
    );
}
